import Popper from "@mui/material/Popper";
import { useCallback, useRef, useState } from "react";

/**
 * Works where `children` is the element to trigger the `PopperChildren` when mouse is hovered
 */
const HoverPopper = ({
    children,
    PopperChildren,
}: {
    children: ({ ref, onPointerEnter, onPointerLeave }) => React.ReactNode;
    PopperChildren: React.ReactNode | (() => React.ReactNode);
}) => {
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setAnchorEl(event.currentTarget);
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 100); // delay closing by 100ms
    }, []);

    const handlePopperEnter = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    const handlePopperLeave = useCallback(() => {
        handleClose();
    }, [handleClose]);

    return (
        <>
            {children({
                ref: anchorEl,
                onPointerEnter: handleOpen,
                onPointerLeave: handleClose,
            })}
            <Popper open={open} anchorEl={anchorEl} onMouseEnter={handlePopperEnter} onMouseLeave={handlePopperLeave}>
                {typeof PopperChildren === "function" ? PopperChildren() : PopperChildren}
            </Popper>
        </>
    );
};

export default HoverPopper;
